import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../AppIcon';
import Button from './Button';

const AuthenticationGate = ({ 
  children, 
  requireAuth = true, 
  requirePremium = false,
  fallbackContent = null,
  showUpgradePrompt = true,
  className = ''
}) => {
  const { user, userProfile, isPremium, loading } = useAuth();
  const [showAuthPrompt, setShowAuthPrompt] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  // Enhanced user data with fallback to userProfile
  const currentUser = userProfile || user;
  const isUserPremium = isPremium || currentUser?.isPremium;
  
  // Debug logging
  console.log('AuthenticationGate state:', { 
    user, 
    userProfile, 
    currentUser, 
    loading, 
    requireAuth,
    isUserPremium 
  });

  useEffect(() => {
    if (loading) return; // Wait for loading to complete

    if (requireAuth && !currentUser) {
      setShowAuthPrompt(true);
    } else if (requirePremium && currentUser && !isUserPremium) {
      setShowUpgradeModal(true);
    } else {
      setShowAuthPrompt(false);
      setShowUpgradeModal(false);
    }
  }, [currentUser, isUserPremium, requireAuth, requirePremium, loading]);

  // Show loading state while authentication is being determined
  if (loading) {
    return <AuthLoadingState className={className} />;
  }

  // Authentication required but user not logged in
  if (requireAuth && !currentUser) {
    return (
      <div className={`min-h-[400px] flex items-center justify-center ${className}`}>
        <div className="max-w-md w-full mx-auto text-center space-y-6 p-8">
          <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
            <Icon name="Lock" size={32} className="text-primary" />
          </div>
          
          <div className="space-y-2">
            <h3 className="text-xl font-semibold text-foreground">
              Sign In Required
            </h3>
            <p className="text-muted-foreground">
              Please sign in to access this feature and continue your career development journey.
            </p>
          </div>

          <div className="space-y-3">
            <Button
              variant="default"
              size="lg"
              fullWidth
              iconName="LogIn"
              iconPosition="left"
              asChild
            >
              <Link to="/authentication">Sign In</Link>
            </Button>
            
            <Button
              variant="outline"
              size="default"
              fullWidth
              asChild
            >
              <Link to="/authentication?tab=register">Create Account</Link>
            </Button>
          </div>

          <div className="text-xs text-muted-foreground">
            Join thousands of professionals advancing their careers with CareerLift
          </div>
        </div>
      </div>
    );
  }

  // Premium required but user doesn't have premium
  if (requirePremium && currentUser && !isUserPremium) {
    return (
      <div className={`relative ${className}`}>
        {/* Blurred Content Preview */}
        <div className="relative">
          <div className="filter blur-sm pointer-events-none select-none">
            {fallbackContent || (
              <div className="min-h-[300px] bg-muted/50 rounded-lg flex items-center justify-center">
                <div className="text-center space-y-4 p-8">
                  <Icon name="Crown" size={48} className="text-warning mx-auto" />
                  <div className="space-y-2">
                    <h3 className="text-lg font-semibold text-foreground">Premium Content</h3>
                    <p className="text-muted-foreground">
                      This feature is available with CareerLift Premium
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Upgrade Overlay */}
          <div className="absolute inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center">
            <div className="max-w-sm w-full mx-auto text-center space-y-6 p-6 bg-card rounded-lg shadow-lg border border-border">
              <div className="w-12 h-12 bg-warning/10 rounded-full flex items-center justify-center mx-auto">
                <Icon name="Crown" size={24} className="text-warning" />
              </div>
              
              <div className="space-y-2">
                <h3 className="text-lg font-semibold text-foreground">
                  Premium Feature
                </h3>
                <p className="text-sm text-muted-foreground">
                  Unlock advanced career insights, AI-powered recommendations, and personalized development plans.
                </p>
              </div>

              <div className="space-y-3">
                <Button
                  variant="default"
                  size="default"
                  fullWidth
                  iconName="Crown"
                  iconPosition="left"
                  asChild
                >
                  <Link to="/subscription-management">Upgrade to Premium</Link>
                </Button>
                
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowUpgradeModal(false)}
                >
                  Maybe Later
                </Button>
              </div>

              <div className="text-xs text-muted-foreground">
                ✓ 30-day money-back guarantee
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // User has required access - render children
  return <div className={className}>{children}</div>;
};

// Premium Content Teaser Component
export const PremiumTeaser = ({ 
  title = "Premium Feature", 
  description = "This feature is available with CareerLift Premium",
  features = [],
  className = ""
}) => {
  return (
    <div className={`bg-gradient-to-br from-warning/5 to-primary/5 rounded-lg border border-warning/20 p-6 text-center space-y-4 ${className}`}>
      <div className="w-12 h-12 bg-warning/10 rounded-full flex items-center justify-center mx-auto">
        <Icon name="Crown" size={24} className="text-warning" />
      </div>
      <div className="space-y-2">
        <h3 className="text-lg font-semibold text-foreground">{title}</h3>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
      {features?.length > 0 && (
        <div className="space-y-2">
          {features?.map((feature, index) => (
            <div key={index} className="flex items-center space-x-2 text-sm text-muted-foreground">
              <Icon name="Check" size={16} className="text-success" />
              <span>{feature}</span>
            </div>
          ))}
        </div>
      )}
      <Button
        variant="default"
        size="default"
        iconName="Crown"
        iconPosition="left"
        asChild
      >
        <Link to="/subscription-management">Upgrade Now</Link>
      </Button>
    </div>
  );
};

// Enhanced Loading State Component for Authentication Checks
export const AuthLoadingState = ({ className = "" }) => {
  return (
    <div className={`min-h-[200px] flex items-center justify-center ${className}`}>
      <div className="text-center space-y-4">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
        <p className="text-sm text-muted-foreground">Checking authentication...</p>
      </div>
    </div>
  );
};

export default AuthenticationGate;